import type { HelpEntry } from '../types'

export const HELP: HelpEntry[] = [
  {
    id: 'connect.page',
    title: 'Connecting FuseGrid to your AI apps',
    body: 'Excel, Claude and ChatGPT all connect to the same FuseGrid endpoint. Each app only reaches the companies you choose, and never more than your current FuseGrid role allows.',
    steps: ['Work through the getting-started checklist from the top.', 'Connect the apps you use, then check them under “Your connections”.'],
    verified_by: ['fusegrid_v2/src/components/connect/connectApi.ts:5', 'fusegrid_v2/server/src/platform/oauth.rs:1513'],
  },
  {
    id: 'connect.checklist',
    title: 'Getting-started checklist',
    body: 'Six steps from a new account to your first AI-assisted client work. Connecting books, inviting your team, connecting an AI app and installing Excel tick themselves when FuseGrid sees them; playbook steps tick when you finish one.',
    steps: ['Use the link beside a step to do it.', 'Choose “Mark done” for a step you completed another way.'],
    limits: ['Progress is per person. Your teammates each have their own checklist.'],
    verified_by: ['fusegrid_v2/server/src/platform/handholding.rs:41', 'fusegrid_v2/server/src/platform/handholding.rs:83', 'fusegrid_v2/src/components/playbooks/PlaybooksPage.tsx:79'],
  },
  {
    id: 'connect.excel',
    title: 'FuseGrid for Excel',
    body: 'The Excel add-in places refreshable statements, cash and reports in your workbooks and adds the FuseGrid assistant to the sidebar.',
    steps: ['Install it from Microsoft AppSource, or download the manifest to sideload it.', 'An IT admin can deploy it to everyone in the firm from the Microsoft 365 admin center.'],
    verified_by: ['fusegrid_v2/src/components/connect/connectApi.ts:7', 'fusegrid_v2/src/components/connect/connectApi.ts:8'],
  },
  {
    id: 'connect.claude',
    title: 'Connecting Claude',
    body: 'Add FuseGrid as a custom connector in Claude with the connector URL. Claude then sends you to FuseGrid to sign in and choose which companies it may use.',
    steps: ['Copy the connector URL.', 'In Claude open Settings → Connectors → Add custom connector, paste the URL and choose Connect.', 'In Claude Code, run the claude mcp add command shown instead.'],
    verified_by: ['fusegrid_v2/src/components/connect/connectApi.ts:5', 'fusegrid_v2/src/components/connect/connectApi.ts:11'],
  },
  {
    id: 'connect.chatgpt',
    title: 'Connecting ChatGPT',
    body: 'Until FuseGrid is listed in the ChatGPT app directory, add it as a developer-mode connector with the same URL and OAuth sign-in.',
    verified_by: ['fusegrid_v2/src/components/connect/connectApi.ts:5', 'fusegrid_v2/server/src/platform/oauth.rs:1513'],
  },
  {
    id: 'connect.your_connections',
    title: 'Your connections',
    body: 'Every AI app you have connected, the companies it can reach (all you can access, or the ones you chose), what it may do, and when it last made a request.',
    steps: ['Choose “Edit permissions” to take a permission away.', 'Choose “Revoke” to disconnect the app; its next request is refused.'],
    limits: ['The app may keep showing FuseGrid until you refresh or remove it there.'],
    verified_by: ['fusegrid_v2/server/src/platform/connections.rs:109', 'fusegrid_v2/server/src/platform/connections.rs:209', 'fusegrid_v2/server/src/platform/oauth.rs:1573'],
  },
  {
    id: 'connect.edit_scopes',
    title: 'Editing permissions',
    body: 'You can only remove permissions here, and read always stays. To give an app more access, revoke it and connect it again, choosing the wider permissions at sign-in.',
    verified_by: ['fusegrid_v2/server/src/platform/connections.rs:172', 'fusegrid_v2/server/src/platform/connections.rs:188'],
  },
  {
    id: 'connect.firm_connections',
    title: "Your firm's connections",
    body: "Firm admins see every AI app their members have connected to the firm's companies, and can revoke any of them. Connections that only reach another firm's companies are not shown.",
    verified_by: ['fusegrid_v2/server/src/platform/connections.rs:111', 'fusegrid_v2/server/src/platform/connections.rs:243'],
  },
  {
    id: 'connect.coach_mode',
    title: 'Coach mode',
    body: 'With coach mode on, the FuseGrid assistant explains its plan before acting, previews every change, and tells you how to check the result. It is on for everyone until they switch it off.',
    verified_by: ['fusegrid_v2/server/src/platform/handholding.rs:146', 'fusegrid_v2/server/src/platform/handholding.rs:153'],
  },
  {
    id: 'connect.ai_activity',
    title: 'What the AI did',
    body: "A per-company log of every request an AI app or the Excel sidebar made, and every change it asked to make in Excel with its outcome. Anyone who can view the company can read it.",
    limits: ['Practice runs on demo companies are logged separately and never appear on real companies.'],
    verified_by: ['fusegrid_v2/server/src/platform/handholding.rs:245', 'fusegrid_v2/server/src/platform/handholding.rs:255', 'fusegrid_v2/server/src/platform/handholding.rs:462'],
  },
]
